import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTextField, type AriaTextFieldProps } from 'react-aria';

import {
  conformTextAreaPropsToAria,
  type ConformTextAreaProps,
} from '../../utils/conformTextAreaPropsToAria';
import { InputDetail } from '../InputDetail/InputDetail';
import styles from './TextArea.module.css';

interface Props extends AriaTextFieldProps {
  helpText?: string;
  textAreaProps?: ConformTextAreaProps;
  errorMessage?: string | string[];
  showCharacterCount?: boolean;
  rows?: number;
  autoResize?: boolean;
}

export function TextArea(props: Props) {
  const {
    label,
    placeholder,
    errorMessage,
    helpText,
    maxLength,
    showCharacterCount,
    rows,
    autoResize,
    onChange,
    defaultValue,
  } = props;
  const [value, setValue] = useState<string>(defaultValue || '');
  const ref = useRef<HTMLTextAreaElement>(null);
  const { labelProps, inputProps } = useTextField(
    {
      label: label || props['aria-label'],
      ...conformTextAreaPropsToAria(props.textAreaProps),
      ...props,
      inputElementType: 'textarea',
    },
    ref
  );

  useLayoutEffect(() => {
    const validity = errorMessage
      ? Array.isArray(errorMessage)
        ? errorMessage[0]
        : errorMessage
      : '';

    ref.current?.setCustomValidity(validity);
  }, []);

  useLayoutEffect(() => {
    if (!ref?.current || !autoResize) {
      return;
    }

    const el = ref.current;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  useEffect(() => {
    if (defaultValue === undefined) {
      return;
    }
    setValue(defaultValue);
  }, [defaultValue]);

  const [activeClass, setActiveClass] = useState<string | undefined>();

  return (
    <div>
      <div className={`${styles['input-group']}`}>
        <textarea
          {
            ...inputProps /* ARIA input props */
          }
          {
            ...(props.textAreaProps ||
              {}) /* Conform applies some input html attribute input props like onFocus, onBlur, etc.  */
          }
          defaultValue={undefined}
          ref={ref}
          placeholder={!label && placeholder ? placeholder : undefined}
          className={`${styles.input} ${activeClass}`}
          rows={autoResize ? 1 : rows}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            onChange && onChange(e.target.value);
          }}
          onBlur={async (e) => {
            if (e.target.value) {
              setActiveClass(styles['is-active']);
            } else {
              setActiveClass(undefined);
            }
          }}
        />
        <label {...labelProps} className={styles.label}>
          {label}
        </label>
      </div>
      <InputDetail
        errorMessage={errorMessage}
        helpText={helpText}
        maxLength={maxLength || inputProps.maxLength}
        showCharacterCount={showCharacterCount}
        charCount={value.length}
      />
    </div>
  );
}
